import { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router';
import { ExternalLink } from 'lucide-react';
import { clsx } from '../../components/ui';
import { Wordmark } from '../../components/Logo';
import { CommunityRules, PrivacyPolicy, TermsOfService } from './content';
import { LEGAL } from './details';

const DOCS = {
  rules: { path: '/rules', title: 'Community Rules', Body: CommunityRules },
  terms: { path: '/terms', title: 'Terms of Service', Body: TermsOfService },
  privacy: { path: '/privacy', title: 'Privacy Policy', Body: PrivacyPolicy },
} as const;

export type LegalDoc = keyof typeof DOCS;
const ORDER: LegalDoc[] = ['rules', 'terms', 'privacy'];

/** The policy page at this path, if it is one. These pages are public. */
export function legalDocFor(pathname: string): LegalDoc | null {
  const path = pathname.replace(/\/+$/, '');
  return ORDER.find((doc) => DOCS[doc].path === path) ?? null;
}

const PROSE = clsx(
  'mt-2 text-[15px] leading-7 text-muted',
  '[&_h2]:mt-10 [&_h2]:scroll-mt-6 [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:text-fg',
  '[&_h3]:mt-6 [&_h3]:font-semibold [&_h3]:text-fg',
  '[&_p]:mt-3 [&_p]:scroll-mt-6 [&_strong]:font-semibold [&_strong]:text-fg',
  '[&_ul]:mt-3 [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:pl-6 [&_ol]:mt-3 [&_ol]:list-decimal [&_ol]:space-y-2 [&_ol]:pl-6 [&_li]:scroll-mt-6 [&_li]:pl-1',
  '[&_a]:text-link [&_a:hover]:underline [&_code]:rounded [&_code]:bg-active [&_code]:px-1 [&_code]:text-[13px]',
  '[&_table]:mt-4 [&_table]:w-full [&_table]:text-left [&_th]:pb-2 [&_th]:pr-4 [&_th]:font-semibold [&_th]:text-fg',
  '[&_td]:border-t [&_td]:border-line [&_td]:py-2 [&_td]:pr-4 [&_td]:align-top [&_td:first-child]:text-fg',
);

const updated = new Date(`${LEGAL.updated}T12:00:00Z`).toLocaleDateString(undefined, { dateStyle: 'long', timeZone: 'UTC' });

export function LegalPage({ doc }: { doc: LegalDoc }) {
  const { title, Body } = DOCS[doc];
  const { hash } = useLocation();
  const scroller = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const previous = document.title;
    document.title = `${title} · Spawnwire`;
    return () => {
      document.title = previous;
    };
  }, [title]);

  // Open at the top, or at the section the link points to (/privacy#cookies).
  useEffect(() => {
    const target = hash ? document.getElementById(decodeURIComponent(hash.slice(1))) : null;
    if (target) target.scrollIntoView();
    else scroller.current?.scrollTo(0, 0);
  }, [doc, hash]);

  return (
    <div ref={scroller} className="h-full overflow-y-auto bg-rail text-fg scroll-thin">
      <header className="border-b border-line bg-sidebar/60">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-3 px-4 pt-4 sm:px-6">
          <Link to="/" aria-label="Spawnwire">
            <Wordmark />
          </Link>
          <Link to="/" className="flex shrink-0 items-center gap-1.5 rounded-md bg-accent px-3 py-1.5 text-sm font-semibold text-accent-fg hover:bg-accent-hover">
            Open Spawnwire
            <ExternalLink className="size-3.5" aria-hidden />
          </Link>
        </div>
        <nav aria-label="Policies" className="mx-auto mt-3 flex max-w-3xl gap-1 overflow-x-auto px-4 sm:px-6">
          {ORDER.map((d) => (
            <Link
              key={d}
              to={DOCS[d].path}
              aria-current={d === doc ? 'page' : undefined}
              className={clsx(
                'shrink-0 border-b-2 px-3 py-2.5 text-sm font-medium',
                d === doc ? 'border-accent text-fg' : 'border-transparent text-muted hover:text-fg',
              )}
            >
              {DOCS[d].title}
            </Link>
          ))}
        </nav>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-8 sm:px-6 sm:py-10">
        <h1 className="text-3xl font-bold tracking-tight">{title}</h1>
        <p className="mt-2 text-sm text-faint">Last updated {updated}</p>
        <article className={PROSE}>
          <Body />
        </article>
      </main>

      <footer className="border-t border-line">
        <div className="mx-auto flex max-w-3xl flex-wrap items-center justify-between gap-x-6 gap-y-2 px-4 py-6 text-sm text-faint sm:px-6">
          <span>© {LEGAL.updated.slice(0, 4)} Spawnwire</span>
          <span className="flex flex-wrap gap-x-4 gap-y-1">
            {ORDER.map((d) => (
              <Link key={d} to={DOCS[d].path} className="hover:text-fg">
                {DOCS[d].title}
              </Link>
            ))}
            <Link to="/status" className="hover:text-fg">
              Status
            </Link>
          </span>
        </div>
      </footer>
    </div>
  );
}
